// HKDSE 2025 Paper 1 Knowledge Graph Samples - Part 3
// Continuing with remaining questions from the LaTeX file

export const hkdse2025Samples_part3 = {
  functions: [
    {
      id: 'hkdse2025_functions_01',
      problem: "已知 $p(x)$ 的一部分隨 $x$ 正變，而另一部分則隨 $x^2$ 正變。假定 $p(7) = 56$ 及 $p(9) = 216$。(a) 求 $p(x)$。(b) 設 $c$ 為一實常數。求 $c$ 值的範圍使得方程 $p(x) = c$ 有兩個相異的實根。",
      problemType: "functions_variation",
      difficulty: "intermediate",
      description: "Working with functions that have direct and square variation",
      graphData: {
        nodes: [
          {
            id: "function_structure",
            name: "Function Structure",
            type: "concepts",
            description: "p(x) has direct and square variation parts",
            value: "p(x) = ax + bx²",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "given_conditions",
            name: "Given Conditions",
            type: "concepts",
            description: "Two points on the function",
            value: "p(7) = 56, p(9) = 216",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "step1_setup_equations",
            name: "Step 1: Setup Equations",
            type: "steps",
            description: "Substitute given points into function",
            value: "7a + 49b = 56, 9a + 81b = 216",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "step2_solve_system",
            name: "Step 2: Solve System",
            type: "steps",
            description: "Solve the system of equations",
            value: "a = -4, b = 4",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "step3_write_function",
            name: "Step 3: Write Function",
            type: "steps",
            description: "Write the complete function",
            value: "p(x) = -4x + 4x² = 4x² - 4x",
            importance: 9,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_quadratic_equation",
            name: "Part (b): Quadratic Equation",
            type: "steps",
            description: "Set up quadratic equation",
            value: "4x² - 4x = c",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_b_discriminant",
            name: "Part (b): Discriminant",
            type: "steps",
            description: "Use discriminant for two distinct real roots",
            value: "Δ = 16 - 16c > 0",
            importance: 9,
            x: 0, y: 250, z: 0
          },
          {
            id: "part_b_solve_inequality",
            name: "Part (b): Solve Inequality",
            type: "steps",
            description: "Solve the inequality",
            value: "16 - 16c > 0 → c < 1",
            importance: 9,
            x: 200, y: 250, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "Function and range for c",
            value: "(a) p(x) = 4x² - 4x (b) c < 1",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "function_structure", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "given_conditions", to: "step1_setup_equations", type: "enables", strength: 9 },
          { from: "step1_setup_equations", to: "step2_solve_system", type: "leads_to", strength: 9 },
          { from: "step2_solve_system", to: "step3_write_function", type: "leads_to", strength: 9 },
          { from: "step3_write_function", to: "part_b_quadratic_equation", type: "enables", strength: 8 },
          { from: "part_b_quadratic_equation", to: "part_b_discriminant", type: "leads_to", strength: 9 },
          { from: "part_b_discriminant", to: "part_b_solve_inequality", type: "leads_to", strength: 9 },
          { from: "step3_write_function", to: "final_answers", type: "contributes_to", strength: 9 },
          { from: "part_b_solve_inequality", to: "final_answers", type: "contributes_to", strength: 9 }
        ]
      }
    }
  ],
  
  statistics_advanced: [
    {
      id: 'hkdse2025_statistics_advanced_01',
      problem: "下面的幹葉圖顯示一些運動員在訓練前的體重（以 $kg$ 為單位）的分佈。Stem-and-leaf plot description: A stem-and-leaf plot with stems 4, 5, 6, 7, 8 and corresponding leaves: Stem 4: leaves 5, 6, 8; Stem 5: leaves w, w, 6, 7; Stem 6: leaves 3, 4, 5, 6, 9, 9; Stem 7: leaves 0, 2; Stem 8: leaves 7. 上述分佈的分佈域與四分位數間距之差為 $25 kg$。(a) 寫出上述分佈的分佈域。由此，求 $w$。(b) 下面的框線圖顯示該些運動員在訓練後的體重（以 $kg$ 為單位）的分佈。Box plot description: A box plot showing weight distribution after training with scale from 46 to 67 kg. The box extends from approximately 54 to 64, with a median line at approximately 61, and whiskers extending to approximately 46 and 67. (i) 求因訓練而引致該分佈的上四分位數的改變。(ii) 該些運動員在訓練後的體重的分佈的離差是否較訓練前的小？試解釋你的答案。",
      problemType: "statistics_stem_leaf_box_plot",
      difficulty: "intermediate",
      description: "Analyzing stem-and-leaf diagram and box plots",
      graphData: {
        nodes: [
          {
            id: "stem_leaf_data",
            name: "Stem-and-Leaf Data",
            type: "concepts",
            description: "Weight distribution before training",
            value: "4|568, 5|ww67, 6|345699, 7|02, 8|7",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "range_iqr_difference",
            name: "Range-IQR Difference",
            type: "concepts",
            description: "Range - IQR = 25 kg",
            value: "Range - IQR = 25",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "part_a_find_range",
            name: "Part (a): Find Range",
            type: "steps",
            description: "Calculate range of distribution",
            value: "Range = 87 - 45 = 42 kg",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "part_a_find_iqr",
            name: "Part (a): Find IQR",
            type: "steps",
            description: "Calculate interquartile range",
            value: "IQR = Q3 - Q1 = 42 - 25 = 17",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_a_solve_w",
            name: "Part (a): Solve for w",
            type: "steps",
            description: "Use the difference condition",
            value: "42 - 17 = 25 → w = 5",
            importance: 9,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_b_box_plot",
            name: "Part (b): Box Plot",
            type: "concepts",
            description: "Weight distribution after training",
            value: "Box plot shows reduced spread",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_b_upper_quartile_change",
            name: "Part (b): Upper Quartile Change",
            type: "steps",
            description: "Find change in upper quartile",
            value: "Q3 after - Q3 before",
            importance: 8,
            x: 0, y: 200, z: 0
          },
          {
            id: "part_b_variability_comparison",
            name: "Part (b): Variability Comparison",
            type: "steps",
            description: "Compare variability before and after",
            value: "Check if IQR and range are smaller after training",
            importance: 9,
            x: 200, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) Range = 42 kg, w = 5 (b) (i) Change in Q3 (ii) Yes/No with explanation",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "stem_leaf_data", to: "range_iqr_difference", type: "enables", strength: 9 },
          { from: "range_iqr_difference", to: "part_a_find_range", type: "enables", strength: 8 },
          { from: "part_a_find_range", to: "part_a_find_iqr", type: "enables", strength: 8 },
          { from: "part_a_find_iqr", to: "part_a_solve_w", type: "leads_to", strength: 9 },
          { from: "part_a_solve_w", to: "part_b_box_plot", type: "enables", strength: 8 },
          { from: "part_b_box_plot", to: "part_b_upper_quartile_change", type: "enables", strength: 8 },
          { from: "part_b_box_plot", to: "part_b_variability_comparison", type: "enables", strength: 9 },
          { from: "part_a_solve_w", to: "final_answers", type: "contributes_to", strength: 9 },
          { from: "part_b_upper_quartile_change", to: "final_answers", type: "contributes_to", strength: 8 },
          { from: "part_b_variability_comparison", to: "final_answers", type: "contributes_to", strength: 9 }
        ]
      }
    }
  ],
  
  locus: [
    {
      id: 'hkdse2025_locus_01',
      problem: "直線 $L$ 分別與正 $x$ 軸及正 $y$ 軸相交於點 $M$ 及點 $N$。設 $P$ 為直角坐標平面上的一動點使得 $MP = NP$。將 $P$ 的軌跡記為 $\\Gamma$。已知 $\\Gamma$ 的方程為 $3x - 2y - 30 = 0$。(a) 描述 $\\Gamma$ 與 $MN$ 之間的幾何關係。(b) 求 $L$ 的方程。(c) 假定 $\\Gamma$ 分別與 $L$ 及 $x$ 軸相交於點 $Q$ 及點 $R$。求四邊形 $ONQR$ 的面積，其中 $O$ 為原點。",
      problemType: "locus_geometry",
      difficulty: "intermediate",
      description: "Working with locus and geometric relationships",
      graphData: {
        nodes: [
          {
            id: "line_intersections",
            name: "Line Intersections",
            type: "concepts",
            description: "L intersects x-axis at M, y-axis at N",
            value: "M(a,0), N(0,b)",
            importance: 9,
            x: 0, y: -200, z: 0
          },
          {
            id: "locus_definition",
            name: "Locus Definition",
            type: "concepts",
            description: "P moves such that MP = NP",
            value: "Locus of points equidistant from M and N",
            importance: 10,
            x: -200, y: 0, z: 0
          },
          {
            id: "locus_equation",
            name: "Locus Equation",
            type: "concepts",
            description: "Given equation of locus",
            value: "3x - 2y - 30 = 0",
            importance: 9,
            x: 200, y: 0, z: 0
          },
          {
            id: "part_a_geometric_relationship",
            name: "Part (a): Geometric Relationship",
            type: "steps",
            description: "Describe relationship between Γ and MN",
            value: "Perpendicular bisector of MN",
            importance: 8,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_find_line_equation",
            name: "Part (b): Find Line Equation",
            type: "steps",
            description: "Find equation of line L",
            value: "Use locus to find M and N coordinates",
            importance: 9,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_c_intersection_points",
            name: "Part (c): Intersection Points",
            type: "steps",
            description: "Find Q and R coordinates",
            value: "Solve systems of equations",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_c_area_calculation",
            name: "Part (c): Area Calculation",
            type: "steps",
            description: "Calculate area of quadrilateral ONQR",
            value: "Use coordinate geometry formulas",
            importance: 9,
            x: 200, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) Perpendicular bisector (b) Line equation (c) Area = calculated value",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "line_intersections", to: "locus_definition", type: "enables", strength: 9 },
          { from: "locus_definition", to: "locus_equation", type: "enables", strength: 9 },
          { from: "locus_equation", to: "part_a_geometric_relationship", type: "enables", strength: 8 },
          { from: "locus_equation", to: "part_b_find_line_equation", type: "enables", strength: 9 },
          { from: "part_b_find_line_equation", to: "part_c_intersection_points", type: "enables", strength: 8 },
          { from: "part_c_intersection_points", to: "part_c_area_calculation", type: "enables", strength: 9 },
          { from: "part_a_geometric_relationship", to: "final_answers", type: "contributes_to", strength: 8 },
          { from: "part_b_find_line_equation", to: "final_answers", type: "contributes_to", strength: 9 },
          { from: "part_c_area_calculation", to: "final_answers", type: "contributes_to", strength: 9 }
        ]
      }
    }
  ],
  
  mensuration: [
    {
      id: 'hkdse2025_mensuration_01',
      problem: "一實心金屬直立圓錐體的高及底半徑分別為 $45 cm$ 及 $24 cm$。將該圓錐體以一平行於其底的平面分成一圓錐體及一平截頭體 $X$。已知 $X$ 的高為 $30 cm$。(a) 以 $\\pi$ 表 $X$ 的體積。(b) 以 $\\pi$ 表 $X$ 的總表面面積。(c) 把 $X$ 熔化，並重鑄成一實心正方體。該正方體的總表面面積是否超過 $X$ 的總表面面積？試解釋你的答案。",
      problemType: "mensuration_cone_frustum",
      difficulty: "intermediate",
      description: "Working with cone, frustum and cube volumes and surface areas",
      graphData: {
        nodes: [
          {
            id: "cone_dimensions",
            name: "Cone Dimensions",
            type: "concepts",
            description: "Original cone dimensions",
            value: "Height = 45 cm, radius = 24 cm",
            importance: 10,
            x: 0, y: -200, z: 0
          },
          {
            id: "frustum_formation",
            name: "Frustum Formation",
            type: "concepts",
            description: "Cutting cone to form frustum X",
            value: "Frustum height = 30 cm",
            importance: 9,
            x: -200, y: 0, z: 0
          },
          {
            id: "similarity_ratio",
            name: "Similarity Ratio",
            type: "concepts",
            description: "Ratio of similar cones",
            value: "h₁/h₂ = r₁/r₂",
            importance: 8,
            x: 200, y: 0, z: 0
          },
          {
            id: "part_a_volume_calculation",
            name: "Part (a): Volume Calculation",
            type: "steps",
            description: "Calculate volume of frustum X",
            value: "V = (π/3)(h)(r₁² + r₁r₂ + r₂²)",
            importance: 9,
            x: 0, y: 100, z: 0
          },
          {
            id: "part_b_surface_area",
            name: "Part (b): Surface Area",
            type: "steps",
            description: "Calculate total surface area of frustum",
            value: "SA = π(r₁² + r₂² + (r₁+r₂)l)",
            importance: 8,
            x: 0, y: 150, z: 0
          },
          {
            id: "part_c_cube_formation",
            name: "Part (c): Cube Formation",
            type: "steps",
            description: "Melt frustum and form cube",
            value: "Cube volume = frustum volume",
            importance: 8,
            x: -200, y: 200, z: 0
          },
          {
            id: "part_c_surface_area_comparison",
            name: "Part (c): Surface Area Comparison",
            type: "steps",
            description: "Compare cube and frustum surface areas",
            value: "Cube SA = 6s², Frustum SA = calculated value",
            importance: 9,
            x: 200, y: 200, z: 0
          },
          {
            id: "final_answers",
            name: "Final Answers",
            type: "values",
            description: "All required answers",
            value: "(a) Volume in terms of π (b) Surface area in terms of π (c) Yes/No with explanation",
            importance: 10,
            x: 0, y: 300, z: 0
          }
        ],
        links: [
          { from: "cone_dimensions", to: "frustum_formation", type: "enables", strength: 9 },
          { from: "frustum_formation", to: "similarity_ratio", type: "enables", strength: 8 },
          { from: "similarity_ratio", to: "part_a_volume_calculation", type: "enables", strength: 9 },
          { from: "part_a_volume_calculation", to: "part_b_surface_area", type: "enables", strength: 8 },
          { from: "part_a_volume_calculation", to: "part_c_cube_formation", type: "enables", strength: 8 },
          { from: "part_c_cube_formation", to: "part_c_surface_area_comparison", type: "enables", strength: 9 },
          { from: "part_b_surface_area", to: "final_answers", type: "contributes_to", strength: 8 },
          { from: "part_c_surface_area_comparison", to: "final_answers", type: "contributes_to", strength: 9 }
        ]
      }
    }
  ]
}; 